import { Banner, Layout, Page, Link, LegacyCard, Icon, Text, Button, Card, BlockStack, Tabs, Box } from "@shopify/polaris";
import { AlertCircleIcon, CheckCircleIcon } from "@shopify/polaris-icons";
import { useState, useEffect } from "react";
import { SkeletonPageSettings } from "./SkeletonPageSettings";
import { FetchCustom } from './FetchCustom';
import { useHostNavigate } from "../hooks";

export function Billing(props) {
    const navigate = useHostNavigate();

    const [loading, setLoading] = useState(true);
    const [model, setModel] = useState({});
    const [processing, setProcessing] = useState(false);
    const [redirectUrl, setRedirectUrl] = useState('');

    const fetch = FetchCustom();

    const tabs = [
        {
            id: 'general',
            content: 'General',
            accessibilityLabel: 'General',
            panelID: 'general-content',
        },
        {
            id: 'billing',
            content: 'Billing',
            panelID: 'billing-content',
        },
        {
            id: 'partners',
            content: 'Partners',
            panelID: 'partners-content',
        }
    ];

    const populateData = async () => {
        props.setIsLoading(true);
        const data = await fetch('api/home/billing');

        if (data.reauthorizeUrl) {
            return;
        }

        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            var dataJsonString = JSON.stringify(data);

            setModel(JSON.parse(dataJsonString));
            setLoading(false);
        }
    };

    const getUser = async () => {
        if (!props.userLoaded) {
            props.setUserLoaded(true);
            const data = await fetch('api/home/userInfo');

            if (data.redirectUrl) {
                setRedirectUrl(data.redirectUrl);
            }
            else if (JSON.stringify(props.user) !== JSON.stringify(data)) {
                props.setUser(data);
                localStorage.setItem('user', JSON.stringify(data));
            }
        }
    };

    //const downgrade = async () => {
    //    props.setIsLoading(true);
    //    await fetchPost('api/home/downgrade', {});
    //    props.setIsLoading(false);
    //    window.location.reload();
    //};

    useEffect(() => {
        document.title = "Billing | Stack Discounts";
        populateData();
        getUser();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (redirectUrl) {
            navigate(redirectUrl);
        }
    }, [redirectUrl, navigate]);

    var currentPlan = model.plans && model.planId ? model.plans.filter((p) => p.id === model.planId)[0] : null;
    var alwaysFree = model.free;
    var errorIcon = model.plans && !model.isFreePlan ? !model.recurringChargeAccepted || model.resubscriptionRequired : false;
    if (!errorIcon) {
        errorIcon = !model.freePlanIsValid || !model.planId;
    }

    return (
        loading ?
            <SkeletonPageSettings tabIndex={1} />
            :
            <Page title='Settings'>
                <Box paddingBlockEnd="400">
                    <LegacyCard>
                        <Tabs tabs={tabs} selected={1} onSelect={(index) => {
                            let url = '';
                            switch (index) {
                                case 1:
                                    url = '/settings/billing';
                                    break;
                                case 2:
                                    url = '/settings/partners';
                                    break;
                                case 0:
                                default:
                                    url = '/settings';
                            }
                            setRedirectUrl(url);
                        }} fitted>
                        </Tabs>
                    </LegacyCard>
                </Box>
                {
                    model.isFreePlan ?
                        <Banner
                            title="Free plan limitations"
                            status="warning"
                        >
                            <p>You can have a maximum of 2 <Link url="/campaigns">campaigns</Link> with 2 discounts assigned.</p>
                            <p>You can always upgrade to a paid plan and avoid all limitations.</p>
                        </Banner>
                        : null
                }
                {
                    !alwaysFree && !model.isFreePlan && !model.recurringChargeAccepted ?
                        <Banner
                            title="Action needed"
                            action={{
                                content: 'Subscribe', onAction: () => {
                                    if (props.user.viewOnly) return;
                                    if (props.isEmbed) {
                                        window.top.location.href = model.resubscribeUrl;
                                    }
                                    else {
                                        window.location.href = model.resubscribeUrl;
                                    }
                                }
                            }}
                            status="critical"
                        >
                            <p>Your subscription is not approved.</p>
                            <p>Please click on <Text as="span" fontWeight="semibold">Subscribe</Text> button to navigate to shop's shopify admin and approve subscription.</p>
                            <p>Only after approving subscription app will start function.</p>
                        </Banner>
                        : null
                }
                {
                    !alwaysFree && !model.isFreePlan && model.recurringChargeAccepted && model.resubscriptionRequired ?
                        <Banner
                            title="Action needed"
                            action={{
                                content: 'Resubscribe', onAction: () => {
                                    if (props.user.viewOnly) return;
                                    if (props.isEmbed) {
                                        window.top.location.href = model.resubscribeUrl;
                                    }
                                    else {
                                        window.location.href = model.resubscribeUrl;
                                    }
                                }
                            }}
                            status="critical"
                        >
                            <p>Shop's pricing plan is changed.</p>
                            <p>App is functioning just fine, but you still in old plan and access to admin panel is restricted.</p>
                            <p>Please click on <Text as="span" fontWeight="semibold">Resubscribe</Text> button to navigate to shop's shopify admin and approve subscription.</p>
                            <p>Only after approving subscription new plan will be applied and you will get access to admin panel.</p>
                        </Banner>
                        : null
                }
                {
                    !alwaysFree ?
                        <Banner
                            title="Pricing plan selection"
                            status="info"
                        >
                            <p>{model.isFreePlan ? 'For paid plan with full functionality, the' : 'The'} pricing plan for the app is selected automatically based on the shop's pricing plan.</p>
                            <p>In case the shop's pricing plan changes the app will continue to function, but the admin panel will redirect to the Billing page to resubscribe.</p>
                        </Banner>
                        : null
                }
                <Layout>
                    <Layout.AnnotatedSection title="Current plan">
                        <Card>
                            <div className="billing-option">
                                <div className="billing-option-title">
                                    <div>{!alwaysFree ? (currentPlan ? currentPlan.displayName : 'None') : 'Free'}</div>
                                    <div>
                                        {
                                            !alwaysFree && errorIcon ?
                                                <Icon source={AlertCircleIcon} tone="critical" />
                                                : null
                                        }
                                        {
                                            alwaysFree || !errorIcon ?
                                                <Icon source={CheckCircleIcon} tone="success" />
                                                : null
                                        }
                                    </div>
                                </div>
                                <div className="billing-option-price">
                                    <span>{'$' + (model.recurringChargePrice ?? '0') + ' per month' + (model.couponApplied ? " (coupon applied)" : "")}</span>
                                    {
                                        model.isFreePlan ?
                                            <span>limited</span>
                                            :
                                            <span>{!alwaysFree ? model.remainingTrialDays + ' trial days left' : 'early adopter'}</span>
                                    }
                                </div>
                                {
                                    !alwaysFree ?
                                        <div className="billing-option-action">
                                            {
                                                model.isFreePlan ?
                                                    <div>Upgrade to shop's current plan: <span>{model.shopPlan?.displayName}</span></div>
                                                    :
                                                    /*<div>Downgrade to free plan</div>*/
                                                    null
                                            }
                                            {
                                                model.isFreePlan ?
                                                    <div><Button loading={processing} primary onClick={() => {
                                                        setProcessing(true);
                                                        if (props.isEmbed) {
                                                            window.top.location.href = model.resubscribeUrl;
                                                        }
                                                        else {
                                                            window.location.href = model.resubscribeUrl;
                                                        }
                                                    }}>Upgrade</Button></div>
                                                    :
                                                    //<div><Button loading={processing} onClick={() => {
                                                    //    setProcessing(true);
                                                    //    downgrade();
                                                    //}}>Downgrade</Button></div>
                                                    null
                                            }
                                        </div>
                                        : null
                                }
                                {
                                    !model.freePlanIsValid ?
                                        <div className="billing-option-error">
                                            <div>
                                                <Text as="span" tone="critical">Free plan limitations are not met. You need to remove some discounts or campaigns or upgrade to paid plan.</Text>
                                            </div>
                                            <div>
                                                <Text as="span" tone="critical">Only after that, you can enable the app. See about limitations at the top of this page.</Text>
                                            </div>
                                        </div>
                                        : null
                                }

                            </div>
                        </Card>
                    </Layout.AnnotatedSection>
                    <Layout.AnnotatedSection title="Pricing plans">
                        <Card background="bg-surface-secondary">
                            {
                                alwaysFree ?
                                    <div className="billing-option">
                                        <div className="billing-option-title">
                                            <div>{'Free'}</div>
                                            <div>
                                                <Icon source={CheckCircleIcon} tone="success" />
                                            </div>
                                        </div>
                                        <div className="billing-option-price">
                                            <span>{'$' + 0 + ' per month'}</span>
                                            <span>early adopter</span>
                                        </div>
                                    </div>
                                    : null
                            }
                            {
                                model.plans.map((item, index) => {
                                    return <div className="billing-option" key={index} data-index={index}>
                                        <div className="billing-option-title">
                                            <div>{item.displayName}</div>
                                            <div>
                                                {
                                                    !alwaysFree && errorIcon && currentPlan?.id === item.id ?
                                                        <Icon source={AlertCircleIcon} tone="critical" />
                                                        : null
                                                }
                                                {
                                                    !alwaysFree && !errorIcon && currentPlan?.id === item.id ?
                                                        <Icon source={CheckCircleIcon} tone="success" />
                                                        : null
                                                }
                                            </div>
                                        </div>
                                        <div className="billing-option-price">
                                            <span>{'$' + item.price + ' per month'}</span>
                                            {
                                                model.shopPlan?.id === item.id ?
                                                    <span>shop's current plan</span>
                                                    : null
                                            }
                                        </div>
                                    </div>
                                })
                            }
                        </Card>
                    </Layout.AnnotatedSection>
                </Layout>
            </Page >
    );
}